import type { DateTime } from 'luxon'
import { useMemo } from 'react'

import type { FormattedRow } from '@/features/bessDashboard/types'
import type { DailyForecastPrices, PriceQuantile } from '@/features/bidding/types/forecastPricing'
import { getMarketTimezone } from '@/features/bidding/utils/forecast/convertForecastTimezone'
import {
  extractHourlyQuantilePrices,
  getMissingForecastHours,
} from '@/features/bidding/utils/forecast/extractHourlyQuantilePrices'

export interface UseHourlyForecastPricesParams {
  forecastsData: FormattedRow[] | undefined
  marketProgramName: string
  selectedDate: DateTime
}

export interface UseHourlyForecastPricesResult {
  /** Hourly forecast prices for the selected market and date */
  dailyPrices: DailyForecastPrices | null
  /** Whether forecast data is available */
  hasData: boolean
  /** Whether forecast data is complete (all hours present) */
  isComplete: boolean
  /** Missing hours if data is incomplete */
  missingHours: number[]
  /** Error message if data is unavailable */
  errorMessage: string | null
}

/**
 * Hook to extract hourly quantile prices from forecast data.
 *
 * @param params - Hook parameters
 * @returns Processed hourly forecast prices with availability status
 */
export function useHourlyForecastPrices({
  forecastsData,
  marketProgramName,
  selectedDate,
}: UseHourlyForecastPricesParams): UseHourlyForecastPricesResult {
  return useMemo(() => {
    // No forecast data available
    if (!forecastsData || forecastsData.length === 0) {
      return {
        dailyPrices: null,
        hasData: false,
        isComplete: false,
        missingHours: [],
        errorMessage: `Forecast data is unavailable for ${marketProgramName}`,
      }
    }

    const marketTimezone = getMarketTimezone()
    const dailyPrices = extractHourlyQuantilePrices(forecastsData, marketProgramName, selectedDate, marketTimezone)

    // No data for this market/date
    if (dailyPrices === null) {
      const dateStr = selectedDate.toISODate() ?? selectedDate.toISO()
      return {
        dailyPrices: null,
        hasData: false,
        isComplete: false,
        missingHours: [],
        errorMessage: `No forecast data available for ${marketProgramName} on ${dateStr}`,
      }
    }

    // Check completeness
    const missingHours = getMissingForecastHours(dailyPrices)
    const isComplete = missingHours.length === 0

    return {
      dailyPrices,
      hasData: true,
      isComplete,
      missingHours,
      errorMessage: isComplete
        ? null
        : `Forecast data incomplete for ${marketProgramName}: missing hours ${missingHours.join(', ')}`,
    }
  }, [forecastsData, marketProgramName, selectedDate])
}

/**
 * Get the price for a specific hour and quantile.
 *
 * @param dailyPrices - The daily forecast prices
 * @param hour - Hour (0-23)
 * @param quantile - Quantile to use
 * @returns Price value or null if not available
 */
export function getForecastPriceForHour(
  dailyPrices: DailyForecastPrices | null,
  hour: number,
  quantile: PriceQuantile,
): number | null {
  if (dailyPrices === null) {
    return null
  }

  const hourlyPrices = dailyPrices.hourlyPrices.get(hour)
  if (hourlyPrices === undefined) {
    return null
  }

  return hourlyPrices[quantile]
}
